import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactService from '~/services/ContactService';
import { useNuxtApp } from '#app';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  department: string;
  subject: string;
  message: string;
  file?: File | null;
  recaptchaToken?: string;
}

/**
 * İletişim formu işlemleri için composable
 */
export function useContact() {
  const { locale } = useI18n();
  const nuxtApp = useNuxtApp();
  
  const isLoading = ref<boolean>(false);
  const isSuccess = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  const form = reactive<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    department: '',
    subject: '',
    message: '',
    file: null
  });
  
  const errors = reactive({
    name: '',
    email: '',
    phone: '',
    department: '',
    subject: '',
    message: ''
  });
  
  const contactService = ContactService.getInstance();
  
  /**
   * Form alanını doğrula
   */
  const validateField = (field: keyof typeof errors): boolean => {
    errors[field] = '';
    
    switch (field) {
      case 'name':
        if (!form.name) {
          errors.name = 'İsim alanı zorunludur';
        } else if (form.name.length < 3) {
          errors.name = 'İsim en az 3 karakter olmalıdır';
        }
        break;
        
      case 'email':
        if (!form.email) {
          errors.email = 'E-posta alanı zorunludur';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          errors.email = 'Geçerli bir e-posta adresi giriniz';
        }
        break;
        
      case 'phone':
        if (!form.phone) {
          // Telefon alanını zorunlu olmaktan çıkar, boş olabilir
          return true;
        } else if (!/^(\+)?[0-9\s-]{10,15}$/.test(form.phone)) {
          errors.phone = 'Geçerli bir telefon numarası giriniz';
        }
        break;
        
      case 'department':
        if (!form.department) {
          errors.department = 'Lütfen bir departman seçiniz';
        }
        break;
        
      case 'subject':
        if (!form.subject) {
          errors.subject = 'Konu alanı zorunludur';
        } else if (form.subject.length < 5) {
          errors.subject = 'Konu en az 5 karakter olmalıdır';
        }
        break;
        
      case 'message':
        if (!form.message) {
          errors.message = 'Mesaj alanı zorunludur';
        } else if (form.message.length < 10) {
          errors.message = 'Mesaj en az 10 karakter olmalıdır';
        }
        break;
    }
    
    return !errors[field];
  };
  
  /**
   * Tüm formu doğrula
   */
  const validateForm = (): boolean => {
    let isValid = true;
    
    const fields = ['name', 'email', 'phone', 'department', 'subject', 'message'] as const;
    fields.forEach(field => {
      if (!validateField(field)) {
        isValid = false;
      }
    });
    
    return isValid;
  };
  
  /**
   * Formu sunucuya gönder
   */
  const submitForm = async (): Promise<boolean> => {
    if (!validateForm()) {
      return false;
    }
    
    isLoading.value = true;
    error.value = null;
    isSuccess.value = false;
    
    try {
      // reCAPTCHA token'ı al
      let recaptchaToken = '';
      try {
        // Plugin'den sağlanan recaptcha kullan
        if (nuxtApp.$recaptcha) {
          recaptchaToken = await nuxtApp.$recaptcha.execute('contact_form');
        } else {
          console.warn('reCAPTCHA kullanılabilir değil, token üretilmedi');
        }
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        error.value = 'Güvenlik doğrulaması başarısız oldu. Lütfen daha sonra tekrar deneyiniz.';
        isLoading.value = false;
        return false;
      }
      
      // Form verilerini hazırla
      const formData: ContactFormData = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        department: form.department,
        subject: form.subject,
        message: form.message,
        recaptchaToken: recaptchaToken
      };
      
      if (form.file) {
        formData.file = form.file;
      }
      
      // Formu gönder
      const response = await contactService.submitContactForm(formData, locale.value);
      
      if (response.status === 200) {
        isSuccess.value = true;
        // Formu temizle
        resetForm();
        return true;
      } else {
        error.value = response.message || 'Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.';
        return false;
      }
    } catch (err) {
      console.error('Form gönderilirken hata oluştu:', err);
      error.value = 'Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.';
      return false;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Formu sıfırla
   */
  const resetForm = () => {
    form.name = '';
    form.email = '';
    form.phone = '';
    form.department = '';
    form.subject = '';
    form.message = '';
    form.file = null;
    
    Object.keys(errors).forEach(key => {
      errors[key as keyof typeof errors] = '';
    });
  };
  
  return {
    form,
    errors,
    isLoading,
    isSuccess,
    error,
    validateField,
    validateForm,
    submitForm,
    resetForm
  };
} 